import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  META_FLOW_CATEGORIES,
  createMetaFlow,
} from 'services/metaFlowService';
import {
  DEFAULT_META_FLOW_SCREEN,
  buildDefaultFlowJson,
} from 'utils/metaFlowJson';
import MetaFlowPreview from './MetaFlowPreview';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const MetaFlowCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const watchedName = Form.useWatch('name', form);
  const watchedDescription = Form.useWatch('description', form);
  const endpointMode = Form.useWatch('endpointMode', form) || 'without';

  const handleCreate = async (values) => {
    setLoading(true);
    try {
      const isDynamic = endpointMode === 'with';
      const flowJson = buildDefaultFlowJson({ isDynamic });

      const payload = {
        name: values.name,
        categories: [values.categories],
        description: values.description || null,
        is_dynamic: isDynamic,
        flow_json: JSON.stringify(flowJson),
        publish: false,
      };

      const response = await createMetaFlow(payload);
      const flowId = response?.id;

      if (!flowId) {
        message.error('MetaFlow was created but no flow id was returned');
        return;
      }

      message.success('MetaFlow draft created');
      navigate(`/flows/edit/${flowId}`);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'Failed to create MetaFlow';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={16}>
        <Card bordered={false}>
          <Space direction="vertical" size={4} style={{ marginBottom: 24 }}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/flows')}
              style={{ paddingLeft: 0 }}
            >
              Back to MetaFlows
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              Create MetaFlow
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Set up the basics, then build screens in the visual editor.
            </Paragraph>
          </Space>

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              name: '',
              categories: 'OTHER',
              description: '',
              endpointMode: 'without',
            }}
            onFinish={handleCreate}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: 'Flow name is required' },
                { max: 100, message: 'Max 100 characters' },
                {
                  pattern: /^[a-zA-Z0-9_\-\s]+$/,
                  message: 'Only letters, numbers, spaces, _ and - are allowed',
                },
              ]}
            >
              <Input placeholder="My customer flow" maxLength={100} showCount />
            </Form.Item>

            <Form.Item
              name="categories"
              label="Categories"
              rules={[{ required: true, message: 'Select a category' }]}
            >
              <Select placeholder="Select category">
                {META_FLOW_CATEGORIES.map((category) => (
                  <Option key={category} value={category}>
                    {category.replace(/_/g, ' ')}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="description" label="Description (optional)">
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Internal note for your team"
              />
            </Form.Item>

            <Form.Item
              name="endpointMode"
              label="Flow type"
              rules={[{ required: true, message: 'Select a flow type' }]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="without">Without endpoint (static flow)</Radio>
                  <Radio value="with">With endpoint (dynamic flow)</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {endpointMode === 'with' && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Dynamic flow"
                description="You can configure tenant API endpoints per screen in the builder after creating the flow."
              />
            )}

            <Space style={{ marginTop: 8 }}>
              <Button onClick={() => navigate('/flows')}>Discard</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={8}>
        <MetaFlowPreview
          flowName={watchedName || 'MetaFlow'}
          description={watchedDescription}
          screens={[DEFAULT_META_FLOW_SCREEN]}
        />
      </Col>
    </Row>
  );
};

export default MetaFlowCreate;
